import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DashboardCard from './DashboardCard';

import { useTranslation } from 'react-i18next';
import { AtNoData } from '../../../components/AtNoData';

// A spline through only 2 real points is mathematically a straight line, so with
// exactly 2 months of data we render a decorative eased curve (smoothstep) between
// them for visual softness, while a separate invisible series keeps the tooltip
// anchored to the two real values instead of the fabricated in-between points.
const CURVE_SEGMENTS = 24;

const smoothstep = (t) => t * t * (3 - 2 * t);

const buildEasedCurve = (startValue, endValue) => {
    const points = [];
    for (let i = 0; i <= CURVE_SEGMENTS; i++) {
        const t = i / CURVE_SEGMENTS;
        points.push([t, startValue + (endValue - startValue) * smoothstep(t)]);
    }
    return points;
};

const buildSeriesPair = (name, color, values) => ([
    {
        name,
        type: 'areaspline',
        data: buildEasedCurve(values[0], values[1]),
        color,
        lineColor: color,
        fillOpacity: 0.15,
        marker: { enabled: false },
        enableMouseTracking: false
    },
    {
        name,
        type: 'line',
        data: [[0, values[0]], [1, values[1]]],
        color,
        lineWidth: 0,
        marker: { enabled: false },
        showInLegend: false,
        enableMouseTracking: true
    }
]);

const TrendChart = ({ data, isLoading }) => {
    const { t } = useTranslation();
    const safeData = data || { categories: [], positive: [], negative: [], neutral: [] };
    const isSparse = safeData.categories.length === 2;
    const isSinglePoint = safeData.categories.length === 1;

    const options = {
        chart: {
            type: 'areaspline',
            height: 300,
            style: { fontFamily: 'inherit' }
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: isSparse ? {
            min: 0,
            max: 1,
            tickPositions: [0, 1],
            title: { enabled: false },
            labels: {
                formatter() {
                    return safeData.categories[Math.round(this.value)] || '';
                }
            }
        } : {
            categories: safeData.categories,
            tickmarkPlacement: 'on',
            title: { enabled: false }
        },
        yAxis: {
            title: { text: null },
            min: 0,
        },
        tooltip: isSparse ? {
            shared: true,
            formatter() {
                const label = safeData.categories[Math.round(this.x)] || '';
                const rows = this.points
                    .map((p) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y}</b> ${t('sentiment.charts.mentions')}`)
                    .join('<br/>');
                return `<b>${label}</b><br/>${rows}`;
            }
        } : {
            shared: true,
            valueSuffix: ` ${t('sentiment.charts.mentions')}`
        },
        plotOptions: {
            areaspline: {
                fillOpacity: isSinglePoint ? 0 : 0.15,
                marker: {
                    enabled: isSinglePoint,
                    symbol: 'circle',
                    radius: isSinglePoint ? 6 : 2,
                    states: {
                        hover: { enabled: true }
                    }
                },
                dataLabels: {
                    enabled: isSinglePoint,
                    useHTML: true,
                    y: -12,
                    formatter() {
                        return `<span style="color:${this.series.color};font-weight:600;font-size:12px">${this.y}</span>`;
                    }
                }
            }
        },
        legend: {
            align: 'right',
            verticalAlign: 'top',
            floating: true,
            y: -15,
            symbolRadius: 10
        },
        series: isSparse ? [
            ...buildSeriesPair(t('sentiment.charts.positive'), '#10B981', safeData.positive),
            ...buildSeriesPair(t('sentiment.charts.negative'), '#EF4444', safeData.negative),
            ...buildSeriesPair(t('sentiment.charts.neutral'), '#F59E0B', safeData.neutral)
        ] : [
            {
                name: t('sentiment.charts.positive'),
                data: safeData.positive,
                color: '#10B981',
                lineColor: '#10B981'
            },
            {
                name: t('sentiment.charts.negative'),
                data: safeData.negative,
                color: '#EF4444',
                lineColor: '#EF4444'
            },
            {
                name: t('sentiment.charts.neutral'),
                data: safeData.neutral,
                color: '#F59E0B',
                lineColor: '#F59E0B'
            }
        ]
    };

    const isEmpty = safeData.categories.length === 0;

    return (
        <DashboardCard title={t('sentiment.charts.trendTitle')} isLoading={isLoading}>
            {isEmpty && !isLoading ? (
                <AtNoData />
            ) : (
                <HighchartsReact highcharts={Highcharts} options={options} />
            )}
        </DashboardCard>
    );
};

export default TrendChart;
